import type { Camera } from '../core/footage';
import { CAMERA_LABELS } from '../core/format';
import { NO_FOOTAGE } from '../core/stage-style';

const QUAD: readonly Camera[] = ['front', 'back', 'right_repeater', 'left_repeater'];
const HEXA: readonly Camera[] = ['left_pillar', 'front', 'right_pillar', 'right_repeater', 'back', 'left_repeater'];

export interface Tile {
  readonly camera: Camera;
  readonly element: HTMLElement;
  readonly video: HTMLVideoElement;
}

export const gridOrder = (cameras: ReadonlySet<Camera>): readonly Camera[] =>
  cameras.has('left_pillar') || cameras.has('right_pillar') ? HEXA : QUAD;

function createTile(camera: Camera, index: number): Tile {
  const element = document.createElement('figure');
  element.className = 'tv-tile';
  element.dataset['camera'] = camera;
  const video = document.createElement('video');
  video.className = 'tv-tile-video';
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.disableRemotePlayback = true;
  const caption = document.createElement('figcaption');
  caption.className = 'tv-tile-label';
  caption.innerHTML = `<kbd>${index + 1}</kbd>`;
  caption.append(` ${CAMERA_LABELS[camera]}`);
  const gap = document.createElement('p');
  gap.className = 'tv-tile-gap';
  gap.textContent = NO_FOOTAGE;
  element.append(video, caption, gap);
  return { camera, element, video };
}

export class CameraGrid {
  readonly tiles: readonly Tile[];
  private focused: Camera | undefined;

  constructor(
    private readonly container: HTMLElement,
    cameras: ReadonlySet<Camera>,
    private readonly onFocusChange: (camera: Camera | undefined) => void,
  ) {
    const order = gridOrder(cameras);
    this.tiles = order.map(createTile);
    container.dataset['layout'] = order === HEXA ? 'hexa' : 'quad';
    delete container.dataset['focus'];
    container.replaceChildren(...this.tiles.map((tile) => tile.element));
    this.tiles.forEach((tile) => tile.element.addEventListener('click', () => this.focus(this.focused === tile.camera ? undefined : tile.camera)));
  }

  get focusedCamera(): Camera | undefined {
    return this.focused;
  }

  focusIndex(index: number): void {
    const tile = this.tiles[index];
    if (tile) this.focus(tile.camera);
  }

  focus(camera: Camera | undefined): void {
    this.focused = camera;
    if (camera) this.container.dataset['focus'] = camera;
    else delete this.container.dataset['focus'];
    this.tiles.forEach((tile) => tile.element.toggleAttribute('data-focused', tile.camera === camera));
    this.onFocusChange(camera);
  }

  visible(tile: Tile): boolean {
    return this.focused === undefined || this.focused === tile.camera;
  }

  destroy(): void {
    this.container.replaceChildren();
  }
}
